import { useState, ComponentType } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  MdDashboard,
  MdLogout,
  MdChat,
  MdFileDownload,
  MdSettings,
  MdDescription,
  MdPeople,
  MdMarkEmailRead,
  MdCampaign,
  MdListAlt,
} from 'react-icons/md';
import { IconBaseProps } from 'react-icons';
import api from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import { useUserStore } from '../store/userStore';
import { useIsAllowed } from '../hooks/useIsAllowed';
import {
  ROLES_WITH_DASHBOARD_ACCESS,
  ROLES_WITH_WHATSAPP_ACCESS,
  SUPERADMIN_ONLY_ROLES,
} from '../constants/roles';

interface SidebarProps {
  user: {
    name: string;
    email: string;
  };
}

interface NavItemProps {
  to: string;
  icon: ComponentType<IconBaseProps>;
  label: string;
}

interface NavSectionProps {
  title: string;
  children: React.ReactNode;
}

function NavItem({ to, icon: Icon, label }: NavItemProps) {
  const location = useLocation();
  const isActive = location.pathname === to;

  return (
    <Link
      to={to}
      className={`group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition-all duration-150 ${
        isActive
          ? 'bg-neutral-200 text-neutral-900'
          : 'text-neutral-600 hover:bg-black/4 hover:text-neutral-900'
      }`}
    >
      <Icon
        size={16}
        className={`transition-colors ${
          isActive
            ? 'text-neutral-900'
            : 'text-neutral-500 group-hover:text-neutral-700'
        }`}
      />
      <span>{label}</span>
    </Link>
  );
}

function NavSection({ title, children }: NavSectionProps) {
  return (
    <div className="space-y-0.5">
      <div className="px-2.5 py-1">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-neutral-500">
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

export default function Sidebar({ user }: SidebarProps) {
  const [showLogout, setShowLogout] = useState(false);
  const navigate = useNavigate();
  const clearUser = useUserStore((state) => state.clearUser);
  const canSeeDashboard = useIsAllowed(ROLES_WITH_DASHBOARD_ACCESS);
  const canSeeWhatsapp = useIsAllowed(ROLES_WITH_WHATSAPP_ACCESS);
  const isSuperAdmin = useIsAllowed(SUPERADMIN_ONLY_ROLES);

  const logout = async () => {
    try {
      await api.post(endpoints.auth.logout);
      clearUser();
      navigate('/login');
    } catch {
      // Error handled by axios interceptor
    }
  };

  return (
    <div className="flex h-screen w-[240px] flex-col border-r border-black/8 bg-neutral-50">
      <div className="px-5 py-4">
        <h1 className="text-base font-semibold text-neutral-900">
          ASSA Chatbot
        </h1>
      </div>

      <nav className="flex-1 space-y-4 px-2 py-3">
        {canSeeDashboard && (
          <>
            <NavItem to="/dashboard" icon={MdDashboard} label="Dashboard" />

            <NavSection title="Engagement">
              <NavItem
                to="/chat-explorer"
                icon={MdChat}
                label="Chat Explorer"
              />
              <NavItem
                to="/chat-export"
                icon={MdFileDownload}
                label="Chat Export"
              />
            </NavSection>
          </>
        )}
        {canSeeWhatsapp && (
          <NavSection title="WhatsApp">
            <NavItem
              to="/whatsapp-bulk-send"
              icon={MdCampaign}
              label="Bulk Send"
            />
            <NavItem to="/whatsapp-campaigns" icon={MdListAlt} label="Comms" />
          </NavSection>
        )}
        {isSuperAdmin && (
          <NavSection title="Configuration">
            <NavItem to="/sources" icon={MdDescription} label="Sources" />
            <NavItem
              to="/email-capture"
              icon={MdMarkEmailRead}
              label="Email Capture"
            />
            <NavItem to="/settings" icon={MdSettings} label="Settings" />
          </NavSection>
        )}
        {isSuperAdmin && (
          <NavSection title="Administration">
            <NavItem to="/users" icon={MdPeople} label="Users" />
          </NavSection>
        )}
      </nav>

      <div className="relative p-2">
        <button
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-all duration-150 hover:bg-black/[0.04]"
          onClick={() => setShowLogout(!showLogout)}
        >
          <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#358f90] text-[11px] font-semibold text-white shadow-sm">
            {user.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 overflow-hidden">
            <div className="truncate text-[13px] font-semibold text-neutral-900">
              {user.name}
            </div>
            <div className="truncate text-[11px] text-neutral-500">
              {user.email}
            </div>
          </div>
        </button>

        {showLogout && (
          <div className="absolute bottom-full left-2 right-2 mb-1.5 overflow-hidden rounded-lg border border-black/[0.08] bg-white shadow-xl ring-1 ring-black/5">
            <button
              onClick={logout}
              className="flex w-full items-center gap-2.5 px-2.5 py-2 text-[13px] font-medium text-red-600 transition-colors duration-150 hover:bg-red-50"
            >
              <MdLogout size={15} />
              <span>Log out</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
